/**
 * Whether this device can reach Chariot right now (hooks/useConnectionStatus.js
 * shows it) — from real answers, not navigator.onLine alone: iOS Safari says
 * "online" on marina wifi that goes nowhere.
 *
 * One store for the page, fed by:
 *   - the browser's online / offline events;
 *   - reportConnection(answered, ms): a request that got an answer (whatever
 *     its status — Chariot answered) or none (no signal, timed out). The
 *     offline outbox's tries and the Inspections tab's requests report;
 *   - a check of Chariot's /health while something shows the status and
 *     nothing has reported for 30 s (or the last request got no answer).
 *
 * "online" | "slow" (the last answer took over 5 s, or one request got none)
 * | "offline" (the device says so, or two in a row got no answer).
 *
 * The check's budget: one GET /health — no sign-in, cached 10 s on the
 * server — per 15 s at most per open page that shows the status, only while
 * the page is in front, only when nothing else said how the signal is; one
 * at a time, 8 s at most; at once when the device comes back online or the
 * page back to the front. No CORS involved: any answer at all is "reached".
 *
 * No imports: utils/api.js may report every request here without a cycle.
 */
const SLOW_MS = 5000;
const QUIET_MS = 30000;
const CHECK_EVERY_MS = 15000;
const CHECK_TIMEOUT_MS = 8000;

let misses = 0; // requests in a row that got no answer
let lastSlow = false;
let lastAt = 0; // when anything last said
let status = typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "online";
const listeners = new Set();

function compute() {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return "offline";
  if (misses >= 2) return "offline";
  if (misses === 1 || lastSlow) return "slow";
  return "online";
}

function publish() {
  const next = compute();
  if (next === status) return;
  status = next;
  listeners.forEach((fn) => { try { fn(status); } catch { /* a listener's problem */ } });
}

/** A request got an answer (`answered`, in `ms`) or none. */
export function reportConnection(answered, ms = 0) {
  lastAt = Date.now();
  if (answered) {
    misses = 0;
    lastSlow = ms > SLOW_MS;
  } else {
    misses += 1;
    lastSlow = false;
  }
  publish();
}

export const connectionStatus = () => status;

// Chariot's /health, beside its /api (any answer, even an error, is reached).
const healthUrl = () => `${String(process.env.REACT_APP_API_URL || "http://localhost:3001/api").replace(/\/api\/?$/, "")}/health`;

let checking = false;
async function check() {
  if (checking || typeof fetch === "undefined") return;
  checking = true;
  const started = Date.now();
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = setTimeout(() => controller?.abort(), CHECK_TIMEOUT_MS);
  try {
    await fetch(healthUrl(), { mode: "no-cors", cache: "no-store", credentials: "omit", signal: controller?.signal });
    reportConnection(true, Date.now() - started);
  } catch {
    reportConnection(false);
  } finally {
    clearTimeout(timer);
    checking = false;
  }
}

const hidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";
function tick() {
  if (hidden()) return;
  if (misses > 0 || Date.now() - lastAt >= QUIET_MS) check();
}
const onOnline = () => { publish(); check(); };
const onOffline = () => publish();
const onVisible = () => { if (!hidden()) check(); };

let interval = null;
function start() {
  if (typeof window === "undefined") return;
  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);
  document.addEventListener("visibilitychange", onVisible);
  interval = setInterval(tick, CHECK_EVERY_MS);
  tick();
}
function stop() {
  if (typeof window === "undefined") return;
  window.removeEventListener("online", onOnline);
  window.removeEventListener("offline", onOffline);
  document.removeEventListener("visibilitychange", onVisible);
  clearInterval(interval);
  interval = null;
}

/** fn(status) on every change; checking runs while anyone listens. → unsubscribe */
export function subscribeConnection(fn) {
  listeners.add(fn);
  if (listeners.size === 1) start();
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0) stop();
  };
}
